/** `mesh` is never a file: it is a `meshBlob` of triangles a CAD kernel made in the page. */
export type ModelLoaderType = 'gltf' | 'stl' | 'obj' | '3mf' | 'mesh';

export function getModelLoaderType(fileName: string): ModelLoaderType | null {
  const ext = fileName.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'glb':
    case 'gltf':
      return 'gltf';
    case 'stl':
      return 'stl';
    case 'obj':
      return 'obj';
    case '3mf':
      return '3mf';
    default:
      return null;
  }
}

const CAD_EXTENSIONS = ['.step', '.stp', '.brep'];

/**
 * A CAD format no browser can load, whose 3D view can only come from a preview
 * the runner derives. Mirrors `_CAD_FORMATS` in the python-runner and
 * `ModelPreviewDispatcher.CAD_EXTENSIONS` in the control plane.
 */
export function isCadFile(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return CAD_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** A renderable stand-in derived from a file, e.g. the GLB the runner makes of a STEP file. */
export interface ModelPreview<S> {
  readonly source: S;
  readonly name?: string | null;
}

export interface ModelCandidate<S> {
  readonly name: string;
  /** Null when the file's own bytes cannot be fetched, e.g. a file left on a local runner. */
  readonly source?: S | null;
  readonly preview?: ModelPreview<S> | null;
}

export interface ModelChoice<S> {
  readonly from: 'preview' | 'file';
  readonly source: S;
  readonly name: string;
  readonly loaderType: ModelLoaderType;
}

/** The bytes to draw in 3D for one file, and the loader for them; null when it has none. */
export function modelOf<S>({ name, source, preview }: ModelCandidate<S>): ModelChoice<S> | null {
  const own = getModelLoaderType(name);
  if (preview) {
    // A preview sent without a name predates names on the wire: a copy of the
    // file itself when that is a mesh, otherwise an STL conversion.
    const loaderType = preview.name ? getModelLoaderType(preview.name) : (own ?? 'stl');
    if (loaderType) return { from: 'preview', source: preview.source, name: preview.name || name, loaderType };
  }
  return own && source != null ? { from: 'file', source, name, loaderType: own } : null;
}

export interface ModelResult<S> {
  readonly files: readonly ModelCandidate<S>[];
  /** Derived for the result as a whole rather than for one of its files. */
  readonly preview?: { readonly source: S; readonly name: string } | null;
}

/** The model a result is seen as: its derived preview, else the first of its files that has one. */
export function modelOfResult<S>({ files, preview }: ModelResult<S>): ModelChoice<S> | null {
  const derived = preview ? modelOf({ name: preview.name, preview }) : null;
  if (derived) return derived;
  for (const file of files) {
    const choice = modelOf(file);
    if (choice) return choice;
  }
  return null;
}
